import React from "react";
import { Leaf, ShieldCheck, Heart, Sprout, Eye, Users } from "lucide-react";
import { VALUES } from "../mock";

const ICONS = { Leaf, ShieldCheck, Heart, Sprout, Eye, Users };

export default function AboutPage() {
  return (
    <div className="bg-[#f4f0e6] text-[#3a3a32]">
      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pt-20 pb-12">
        <h1 className="text-4xl md:text-6xl font-light leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Our story: building the world's most polyphenol-rich olive oil
        </h1>
        <h2 className="mt-4 text-2xl md:text-3xl text-[#4a7ba1] font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Working with nature, not against it.
        </h2>
      </section>

      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-10">
        <h3 className="text-xl uppercase tracking-widest text-[#4a7ba1] mb-4">What is Fountain of Youth?</h3>
        <p className="text-lg text-[#3a3a32] leading-relaxed">
          Fountain of Youth is a producer of ultra-high phenolic extra virgin olive oil developed for wellness, longevity, and clinical research applications. Every harvest is independently verified by LC-MS/MS at IOC-accredited laboratories and delivered in nitrogen-flushed 5 ml monodoses to preserve polyphenol stability from grove to consumption.
        </p>
        <p className="mt-4 text-lg text-[#3a3a32] leading-relaxed">
          The 2025–2026 harvest is verified at 2,236 mg/kg total polyphenols and 11,636 mg/kg total bioactive compounds, tested by LC-MS/MS at two independent laboratories using the analytical method recognised by the EFSA and the International Olive Council. The oil meets the EFSA-authorised health claim under EU Regulation 432/2012, delivering 892% above the required hydroxytyrosol minimum.
        </p>
      </section>

      {/* Values */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
        <h3 className="text-3xl font-light text-center mb-12" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Our Values</h3>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8">
          {VALUES.map((v, i) => {
            const Icon = ICONS[v.icon] || Leaf;
            return (
              <div key={i} className="text-center p-6 border border-[#4a7ba1]/15 rounded-sm bg-[#ece6d6]/40 hover:bg-[#ece6d6] transition-colors">
                <div className="w-16 h-16 mx-auto rounded-full bg-[#4a7ba1]/15 grid place-items-center">
                  <Icon size={28} className="text-[#4a7ba1]" strokeWidth={1.4} />
                </div>
                <h4 className="mt-5 text-xl text-[#1e3a52]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{v.title}</h4>
                <p className="mt-2 text-sm text-[#5a5a52] leading-relaxed">{v.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Radically Different */}
      <section className="bg-[#ece6d6]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-2 gap-12 items-start">
          <div>
            <p className="text-xs tracking-[0.4em] uppercase text-[#4a7ba1] mb-3">Radically different</p>
            <h3 className="text-3xl md:text-5xl font-light leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Phenolic excellence by design.</h3>
            <img src="https://images.pexels.com/photos/11086294/pexels-photo-11086294.jpeg" alt="Mediterranean olive grove" className="mt-8 w-full h-[420px] object-cover rounded-sm" />
          </div>
          <div className="space-y-8">
            {[
              { t: "Terroir", b: "Mediterranean geology, intense summer heat, and proximity to the sea produce olives with a phenolic profile that cannot be easily replicated. Our groves sit in microclimates selected for their natural stress response." },
              { t: "Cutting-edge extraction", b: "Olives are hand-harvested at the peak of freshness, when polyphenol concentration is at its maximum. Mobile refrigeration cools the fruit within minutes of picking. Cold extraction under nitrogen protection preserves every compound." },
              { t: "Permaculture", b: "Biodiverse polyculture, integrated animal systems, zero synthetic or organic pesticides, and a strict zero-kill policy. The result is a living soil microbiome that delivers nutrient density into the fruit." },
              { t: "Carbon negative", b: "Not just carbon-neutral — carbon negative. Each litre represents 16 kg of CO₂ sequestered, verified by the IOC CarbonBalance methodology." },
            ].map((x, i) => (
              <div key={i}>
                <h4 className="uppercase tracking-widest text-sm text-[#4a7ba1]">{x.t}</h4>
                <p className="mt-2 text-[#3a3a32] leading-relaxed">{x.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Scientific Verification */}
      <section className="bg-[#1e3a52] text-[#f4f0e6]">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20">
          <h3 className="text-3xl md:text-4xl font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Scientific Verification</h3>
          <p className="mt-6 text-[#dcd6c4] leading-relaxed">
            Fountain of Youth is tested every harvest by two independent laboratories using LC-MS/MS and HPLC — the analytical gold standard recognised by the International Olive Council and the European Food Safety Authority.
          </p>
          <div className="mt-8 grid md:grid-cols-2 gap-6">
            <div className="border border-[#dcd6c4]/20 p-5 rounded-sm">
              <h4 className="uppercase tracking-widest text-sm text-[#d4a857]">Laboratory 1</h4>
              <p className="mt-2">Universidad de Córdoba, Department of Analytical Chemistry — Interreg Mediterranean ARISTOIL project.</p>
            </div>
            <div className="border border-[#dcd6c4]/20 p-5 rounded-sm">
              <h4 className="uppercase tracking-widest text-sm text-[#d4a857]">Laboratory 2</h4>
              <p className="mt-2">Chem Solutions Analytical Laboratories, Athens — IOC-recognised accreditation.</p>
            </div>
          </div>
          <p className="mt-8 text-sm text-[#dcd6c4]/80 leading-relaxed">
            <strong>Standards compliance:</strong> EU Regulation 2022/2104 (EVOO quality), EU Regulation 2022/2105 (classification), EU Regulation 432/2012 (EFSA health claim), IOC Official Methods (COI/T.20/Doc series), Codex Stan 33-1981.
          </p>
        </div>
      </section>
    </div>
  );
}
